import { Injectable, OnDestroy, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { ApplicationState } from '../../../../../core/models/application-state/application-state';
import { ExistingLicenseApplicationPurposeCodes } from '../../constants/application-purpose-codes';

@Injectable({
    providedIn: 'root',
})

/**
 * A service that manages the state of a 601 Application.
 */
export class ApplicationStateService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private IsApplicationTemplateSignal = signal(false);
    private IsExistingLicenseApplicationSignal = signal(false);

    private _applicationState$ = new BehaviorSubject<ApplicationState | null>(
        null,
    );

    constructor() {
        this._applicationState$
            .pipe(takeUntil(this.destroy$))
            .subscribe((applicationState: ApplicationState | null) => {
                this.IsApplicationTemplateSignal.set(
                    !!applicationState?.applicationTemplateInformation,
                );

                this.IsExistingLicenseApplicationSignal.set(
                    applicationState?.applicationPurpose
                        ? ExistingLicenseApplicationPurposeCodes.includes(
                              applicationState?.applicationPurpose,
                          )
                        : false,
                );
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public readonly applicationState$: Observable<ApplicationState | null> =
        this._applicationState$.asObservable();

    /**
     * Sets the provided ApplicationState as the current application state.
     * @param newApplicationState The new application state to set.
     */
    setApplicationState(newApplicationState: ApplicationState): void {
        this._applicationState$.next(newApplicationState);
    }

    /**
     * Clears the application state until it is time to fetch a new one.
     * The application parent component, when deconstructing, will call this method to clear the state.
     */
    clearApplicationState(): void {
        this._applicationState$.next(null);
    }

    /**
     * Gets the current value of the application state.
     * @returns The current ApplicationState or null if not set
     */
    getCurrentApplicationState(): ApplicationState | null {
        return this._applicationState$.value;
    }

    /**
     * The current application template status (as a signal).
     */
    IsApplicationTemplate = this.IsApplicationTemplateSignal.asReadonly();

    /**
     * The current status of whether the application is an existing license (as a signal).
     */
    IsExistingLicenseApplication =
        this.IsExistingLicenseApplicationSignal.asReadonly();
}
